import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Headphones, Play, Pause, Star, Music2 } from "lucide-react";
import { RECITERS, RECITER_FOLDERS } from "@/data/reciters";

/**
 * Compare Reciters dialog. Triggered from the Audio settings panel.
 *
 * Lets the user pick 2–3 reciters and plays the same ayah back-to-back so
 * they can A/B different voices for memorization. The current ayah comes
 * from the parent (Reader) — defaults to surah:1 if nothing is playing.
 *
 * After comparing, a single tap on the star icon next to any selected
 * reciter promotes it to the user's default (persisted via onSetDefault).
 */
export default function CompareReciters({
  surahNum,
  ayahNum,
  currentReciter,
  onSetDefault,
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([
    "ar.alafasy",
    "ar.husary",
    "ar.abdulbasit",
  ]);
  const [activeIdx, setActiveIdx] = useState(-1); // -1 = idle
  const audioRef = useRef(null);
  const cancelledRef = useRef(false);

  // Honor a one-shot localStorage flag set by the Dashboard's first-time
  // reciter nudge — auto-open the Compare dialog on mount exactly once.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("quran_open_compare_on_mount") === "1") {
      localStorage.removeItem("quran_open_compare_on_mount");
      setOpen(true);
    }
  }, []);

  // Stop & cleanup whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      cancelledRef.current = true;
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.onended = null;
          audioRef.current.onerror = null;
          audioRef.current.src = "";
        } catch (err) {
          console.warn("audio cleanup failed:", err);
        }
      }
      setActiveIdx(-1);
    }
  }, [open]);

  const toggleReciter = (value) => {
    setSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      if (prev.length >= 3) {
        // Cap at 3 — replace the oldest.
        return [...prev.slice(1), value];
      }
      return [...prev, value];
    });
  };

  const buildUrl = (reciterValue) => {
    const folder = RECITER_FOLDERS[reciterValue] || "Alafasy_128kbps";
    return `https://everyayah.com/data/${folder}/${String(surahNum).padStart(
      3,
      "0",
    )}${String(ayahNum).padStart(3, "0")}.mp3`;
  };

  const playSequence = async () => {
    if (selected.length < 2) return;
    cancelledRef.current = false;
    for (let i = 0; i < selected.length; i++) {
      if (cancelledRef.current) break;
      setActiveIdx(i);
      const url = buildUrl(selected[i]);
      const audio = audioRef.current || new Audio();
      audioRef.current = audio;
      audio.src = url;
      try {
        await new Promise((resolve, reject) => {
          audio.onended = () => resolve();
          audio.onerror = () => reject(new Error("audio error"));
          audio.play().catch(reject);
        });
      } catch (err) {
        console.warn(`reciter ${i} playback failed, skipping:`, err);
      }
      // small gap between reciters
      if (!cancelledRef.current) {
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    setActiveIdx(-1);
  };

  const stopSequence = () => {
    cancelledRef.current = true;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (err) {
        console.warn("stopSequence pause failed:", err);
      }
    }
    setActiveIdx(-1);
  };

  const isPlaying = activeIdx >= 0;
  const surahDisplay = surahNum || 1;
  const ayahDisplay = ayahNum || 1;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="compare-reciters-btn"
          variant="ghost"
          size="sm"
          className="w-full justify-start text-slate-300 hover:text-gold-400 hover:bg-gold-400/10 font-outfit"
        >
          <Headphones className="w-4 h-4 mr-2" />
          Compare reciters
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="compare-reciters-dialog"
        className="bg-islamic-blue-900 border-white/10 text-slate-100 max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-cinzel text-gold-400 flex items-center gap-2">
            <Music2 className="w-5 h-5" /> Compare Reciters
          </DialogTitle>
          <DialogDescription className="text-slate-400 font-outfit text-sm">
            Pick up to 3 reciters and play{" "}
            <span className="text-gold-400">
              {surahDisplay}:{ayahDisplay}
            </span>{" "}
            back-to-back to find your favorite voice.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-64 pr-3 -mr-2">
          <div className="space-y-1">
            {RECITERS.map((r) => {
              const checked = selected.includes(r.value);
              const idx = selected.indexOf(r.value);
              const isCurrent = activeIdx >= 0 && selected[activeIdx] === r.value;
              const isDefault = currentReciter === r.value;
              return (
                <div
                  key={r.value}
                  className={`w-full flex items-center rounded-lg text-left text-sm font-outfit transition-colors ${
                    checked
                      ? "bg-gold-400/15 text-gold-300"
                      : "text-slate-300 hover:bg-white/5"
                  } ${isCurrent ? "ring-1 ring-gold-400/60" : ""}`}
                >
                  <button
                    data-testid={`compare-reciter-${r.value}`}
                    onClick={() => toggleReciter(r.value)}
                    disabled={isPlaying}
                    className={`flex-1 flex items-center justify-between px-3 py-2 text-left ${
                      isPlaying ? "cursor-not-allowed opacity-70" : ""
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {r.label}
                      {isDefault && (
                        <span className="text-[10px] bg-gold-400/25 text-gold-300 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                          default
                        </span>
                      )}
                    </span>
                    {checked && (
                      <span className="text-xs bg-gold-400/20 text-gold-300 px-2 py-0.5 rounded-full">
                        {idx + 1}
                      </span>
                    )}
                  </button>
                  {checked && onSetDefault && !isDefault && (
                    <button
                      data-testid={`compare-setdefault-${r.value}`}
                      title="Set as default reciter"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDefault(r.value);
                        toast.success(`Default reciter: ${r.label}`);
                      }}
                      disabled={isPlaying}
                      className={`px-2 py-2 text-slate-400 hover:text-gold-400 transition-colors ${
                        isPlaying ? "cursor-not-allowed opacity-50" : ""
                      }`}
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  {isDefault && (
                    <span className="px-2 py-2 text-gold-400" title="Your default reciter">
                      <Star className="w-4 h-4 fill-current" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <p className="text-xs text-slate-500 font-outfit">
            {selected.length} selected · max 3
          </p>
          <div className="flex items-center gap-2">
            {isPlaying ? (
              <Button
                data-testid="compare-stop-btn"
                onClick={stopSequence}
                variant="ghost"
                className="text-slate-400 hover:text-red-400"
              >
                <Pause className="w-4 h-4 mr-1" /> Stop
              </Button>
            ) : (
              <Button
                data-testid="compare-play-btn"
                onClick={playSequence}
                disabled={selected.length < 2}
                className="bg-gold-400 hover:bg-gold-300 text-islamic-blue-950 font-outfit"
              >
                <Play className="w-4 h-4 mr-1" /> Play sequence
              </Button>
            )}
          </div>
        </div>

        {isPlaying && (
          <p
            data-testid="compare-now-playing"
            className="text-center text-gold-400 font-outfit text-sm pt-1"
          >
            Now playing: {RECITERS.find((r) => r.value === selected[activeIdx])?.label}
            {" "}({activeIdx + 1}/{selected.length})
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
